import { useEffect } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { FaFilter } from 'react-icons/fa';

// Drawer Overlay
const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
`;

// Drawer Container
const DrawerContainer = styled.div`
  position: fixed;
  top: 0;
  left: ${(props) => (props.isOpen ? '0' : '-40%')};
  width: 40%;
  height: 100%;
  background-color: white;
  z-index: 1001;
  transition: left 0.3s ease;
  overflow-y: auto;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    width: 85%;
    left: ${(props) => (props.isOpen ? '0' : '-85%')};
  }
`;

// Drawer Header
const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa;
`;

const DrawerTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`;

// Drawer Content
const DrawerContent = styled.div`
  padding: 1rem;
`;

// Mobile Filter Button
const FilterButton = styled.button`
  position: fixed;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  background: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  border: none;
  border-radius: 0 8px 8px 0;
  padding: 12px 8px;
  cursor: pointer;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.2);

  &:active {
    transform: translateY(-50%) translateX(1px);
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

// Apply Button Container
const ApplyButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 10px;
  border-top: 1px solid #eee;
  background: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
`;

const ApplyButton = styled.button`
  background: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  min-width: 120px;

  &:active {
    transform: translateY(1px);
  }
`;

const MobileFilterDrawer = ({
  isOpen,
  onToggle,
  onClose,
  title = 'Filters',
  children,
  showApplyButton = true,
  onApply,
}) => {
  // Close drawer when clicking overlay
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle apply button click
  const handleApplyClick = () => {
    if (onApply) {
      onApply();
    }
    onClose();
  };

  // Close drawer on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Mobile Filter Button */}
      <FilterButton
        onClick={onToggle}
        title={isOpen ? 'Close Filters' : 'Open Filters'}
      >
        <FaFilter size={16} />
      </FilterButton>

      {/* Drawer */}
      <DrawerOverlay isOpen={isOpen} onClick={handleOverlayClick}>
        <DrawerContainer isOpen={isOpen}>
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            <CloseButton onClick={onClose}>
              <IoClose size={20} />
            </CloseButton>
          </DrawerHeader>
          <DrawerContent>
            {children}
            {showApplyButton && (
              <ApplyButtonContainer>
                <ApplyButton onClick={handleApplyClick}>Apply</ApplyButton>
              </ApplyButtonContainer>
            )}
          </DrawerContent>
        </DrawerContainer>
      </DrawerOverlay>
    </>
  );
};

export default MobileFilterDrawer;
